import { ApiError, ReadOnlyError } from '@/api/client';

/**
 * Traduz erro de API para uma frase que o usuário entende e que diz o que fazer.
 *
 * Antes, dez telas repetiam `Servidor respondeu 401 (Unauthorized)`. Isso não é mensagem de
 * erro, é despejo de protocolo: mistura português com inglês, informa um número que não
 * significa nada para quem opera um barco, e não diz a única coisa que importa, que é o que
 * a pessoa deve fazer agora. No caso do 401, por exemplo, o comando não falhou por defeito
 * do rastreador nem do servidor: a sessão expirou e basta entrar de novo.
 *
 * A mensagem do servidor, quando existe, vem na frente da genérica: o backend do NautMax
 * responde `detail` explicando a recusa (condição fora do formato, dispositivo fora do
 * acesso), e essa explicação é melhor que qualquer texto fixo daqui.
 */

const POR_STATUS: Record<number, string> = {
  400: 'Os dados enviados não foram aceitos. Revise o que foi preenchido.',
  401: 'Sua sessão expirou. Entre novamente para continuar.',
  403: 'Você não tem permissão para fazer isso.',
  404: 'Não encontrado. O item pode ter sido removido por outra pessoa.',
  408: 'O servidor demorou para responder. Tente de novo.',
  409: 'Já existe um registro com esses dados, ou alguém alterou antes de você salvar.',
  413: 'O arquivo é grande demais.',
  422: 'Os dados enviados não foram aceitos. Revise o que foi preenchido.',
  429: 'Muitas tentativas seguidas. Aguarde alguns segundos e tente de novo.',
  500: 'O servidor encontrou um erro ao processar. Tente de novo; se continuar, avise o suporte.',
  502: 'O servidor está fora do ar no momento. Tente novamente em instantes.',
  503: 'O servidor está fora do ar no momento. Tente novamente em instantes.',
  504: 'O servidor demorou demais para responder. Tente novamente em instantes.',
};

/** Detalhe vindo do servidor, se for texto útil e não repetição do status. */
const detalheDoServidor = (e: ApiError): string | null => {
  const bruto = (e.message ?? '').trim();
  if (!bruto || bruto === e.statusText) return null;
  const primeiraLinha = bruto.split('\n')[0].trim();
  // HTML de página de erro não serve de mensagem.
  if (!primeiraLinha || primeiraLinha.startsWith('<')) return null;
  return primeiraLinha.slice(0, 240);
};

export function mensagemDeErro(e: unknown): string {
  if (e instanceof ReadOnlyError) {
    return 'A plataforma está em modo somente leitura. Nenhuma alteração foi gravada.';
  }
  if (e instanceof ApiError) {
    const detalhe = detalheDoServidor(e);
    if (detalhe) return detalhe;
    return POR_STATUS[e.status] ?? `Não foi possível completar a ação (erro ${e.status}).`;
  }
  if (e instanceof Error) {
    // Erro de rede do fetch chega como TypeError com texto do navegador, em inglês.
    if (/fetch|network|failed to fetch/i.test(e.message)) {
      return 'Não foi possível falar com o servidor. Verifique sua conexão com a internet.';
    }
    return e.message;
  }
  return 'Não foi possível completar a ação.';
}

/** Mesma tradução, prefixada pela ação que falhou: `Não foi possível enviar "Reset": …` */
export function mensagemDeErroAo(acao: string, e: unknown): string {
  return `Não foi possível ${acao}: ${mensagemDeErro(e)}`;
}
